import { memo, useEffect, useMemo } from 'react';
import { Button, MultiSelect, TextInput } from '@carbon/react';
import { TrashCan, WarningFilled } from '@carbon/react/icons';
import classNames from 'classnames';
import { FreeTextComboBox } from 'components/free-text-combobox';
import { TooltipIcon } from 'components/tooltip-icon';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce, isEqual } from 'lodash-es';
import { operatorQueryAtom, operatorSearchLoadableAtom } from './atoms';
import { isLineHurdleDisabled } from './discount-rate-line-utils';
import type {
  DiscountRateLine as Line,
  EnumOption,
  InterestType,
  RateCategory,
  ReserveCategory,
} from './types';
import styles from './discount-rate-table.module.scss';

export interface LineEnums {
  rateCategories: EnumOption[];
  reserveCategories: EnumOption[];
  interestTypes: EnumOption[];
}

interface DiscountRateLineProps {
  areaIndex: number;
  lineIndex: number;
  line: Line;
  showLabels: boolean;
  enums: LineEnums | null;
  /** Combinations this line shares with another line; Save is blocked while any remain. */
  overlaps: string[];
  // The handlers take the line's index so the table can pass the same functions to every line.
  onChange: (
    lineIndex: number,
    patch: Partial<
      Pick<Line, 'rateCategories' | 'interestTypes' | 'operator' | 'rate'>
    >
  ) => void;
  onReserveCategoriesChange: (
    lineIndex: number,
    value: ReserveCategory[]
  ) => void;
  onHurdleChange: (lineIndex: number, value: number | null) => void;
  onRemove: (lineIndex: number) => void;
}

interface SelectionCellProps {
  id: string;
  titleText: string;
  showLabels: boolean;
  options: EnumOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}

/**
 * A multi-select over one enum, labelled with the picked options. A disabled option can't be
 * newly picked, but one the line already holds (saved before it was disabled) is kept.
 * @param {SelectionCellProps} props - The cell props
 * @return {JSX.Element} The multi-select cell
 */
function SelectionCell({
  id,
  titleText,
  showLabels,
  options,
  selected,
  onChange,
}: SelectionCellProps): JSX.Element {
  const selectedItems = options.filter((o) => selected.includes(o.value));

  return (
    <div className={styles.cell}>
      <MultiSelect<EnumOption>
        id={id}
        titleText={titleText}
        hideLabel={!showLabels}
        label={selectedItems.map((o) => o.label).join(', ') || 'Select...'}
        size="sm"
        items={options}
        itemToString={(item) => item?.label ?? ''}
        selectedItems={selectedItems}
        onChange={({ selectedItems: picked }) =>
          onChange(
            (picked ?? [])
              .filter((o) => !o.disabled || selected.includes(o.value))
              .map((o) => o.value)
          )
        }
        selectionFeedback="fixed"
        sortItems={(items) => [...items]}
      />
    </div>
  );
}

/**
 * @param {string} text - An input's value
 * @return {number | ''} The number typed, or blank
 */
function parseRate(text: string): number | '' {
  const n = Number.parseFloat(text);
  return Number.isNaN(n) ? '' : n;
}

/**
 * One discount rate line: multi-selects for rate category, reserve category and interest type,
 * then operator, rate and hurdle rate. Labels are shown on the first line only.
 * @param {DiscountRateLineProps} props - The line props
 * @return {JSX.Element} The line
 */
function DiscountRateLineRow({
  areaIndex,
  lineIndex,
  line,
  showLabels,
  enums,
  overlaps,
  onChange,
  onReserveCategoriesChange,
  onHurdleChange,
  onRemove,
}: DiscountRateLineProps): JSX.Element {
  const operatorSearchState = useAtomValue(operatorSearchLoadableAtom);
  const setOperatorQuery = useSetAtom(operatorQueryAtom);

  const debouncedOperatorQuery = useMemo(
    () => debounce((text: string) => setOperatorQuery(text), 350),
    [setOperatorQuery]
  );

  useEffect(() => {
    return () => debouncedOperatorQuery.cancel();
  }, [debouncedOperatorQuery]);

  const operatorItems = useMemo(() => {
    if (operatorSearchState.state !== 'hasData' || !operatorSearchState.data)
      return [];
    return operatorSearchState.data.results.map((r) => ({
      id: r.name,
      value: r.name,
      label: r.name,
    }));
  }, [operatorSearchState]);

  const idSuffix = `${areaIndex}-${lineIndex}`;

  return (
    <div className={styles.row}>
      <div className={styles.cellStatus}>
        {overlaps.length > 0 && (
          <TooltipIcon
            align="right"
            openOnClick
            className={styles.overlapIcon}
            label={`Line ${lineIndex + 1} overlaps another line`}
            definition={
              <>
                Another line also covers:
                <ul className={styles.overlapList}>
                  {overlaps.map((overlap) => (
                    <li key={overlap}>{overlap}</li>
                  ))}
                </ul>
              </>
            }
          >
            <WarningFilled />
          </TooltipIcon>
        )}
      </div>

      <SelectionCell
        id={`rate-cat-${idSuffix}`}
        titleText="Rate Categories"
        showLabels={showLabels}
        options={enums?.rateCategories ?? []}
        selected={line.rateCategories}
        onChange={(values) =>
          onChange(lineIndex, { rateCategories: values as RateCategory[] })
        }
      />
      <SelectionCell
        id={`reserve-cat-${idSuffix}`}
        titleText="Reserve Categories"
        showLabels={showLabels}
        options={enums?.reserveCategories ?? []}
        selected={line.reserveCategories}
        onChange={(values) =>
          onReserveCategoriesChange(lineIndex, values as ReserveCategory[])
        }
      />
      <SelectionCell
        id={`interest-type-${idSuffix}`}
        titleText="Interest Types"
        showLabels={showLabels}
        options={enums?.interestTypes ?? []}
        selected={line.interestTypes}
        onChange={(values) =>
          onChange(lineIndex, { interestTypes: values as InterestType[] })
        }
      />

      <div className={styles.cell}>
        <FreeTextComboBox
          id={`operator-${idSuffix}`}
          titleText={showLabels ? 'Operator' : ''}
          placeholder="Select Operator"
          items={operatorItems}
          selectedItem={
            line.operator
              ? { value: line.operator, label: line.operator }
              : null
          }
          itemToString={(item) => String(item?.label ?? '')}
          onChange={({ selectedItem }) =>
            onChange(lineIndex, { operator: String(selectedItem?.value ?? '') })
          }
          onInputChange={(text) => {
            onChange(lineIndex, { operator: text });
            debouncedOperatorQuery(text);
          }}
        />
      </div>

      <div className={classNames(styles.cell, styles.cellRate)}>
        <TextInput
          id={`rate-${idSuffix}`}
          labelText="Rate (%)"
          hideLabel={!showLabels}
          size="sm"
          type="number"
          step="0.01"
          placeholder="—"
          value={line.rate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(lineIndex, { rate: parseRate(e.target.value) })
          }
        />
      </div>

      <div className={classNames(styles.cell, styles.cellRate)}>
        <TextInput
          id={`hurdle-${idSuffix}`}
          labelText="Hurdle Rate (%)"
          hideLabel={!showLabels}
          size="sm"
          type="number"
          step="0.01"
          placeholder="—"
          disabled={isLineHurdleDisabled(line)}
          value={line.hurdleRate ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = parseRate(e.target.value);
            onHurdleChange(lineIndex, value === '' ? null : value);
          }}
        />
      </div>

      <div className={styles.cellAction}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={TrashCan}
          iconDescription="Remove line"
          tooltipPosition="top"
          onClick={() => onRemove(lineIndex)}
          className={styles.removeBtn}
        />
      </div>
    </div>
  );
}

/**
 * @param {DiscountRateLineProps} prev - The previous props
 * @param {DiscountRateLineProps} next - The next props
 * @return {boolean} True when the line would render the same; overlaps and options compare by
 *   content, since the table derives fresh arrays for each line whenever any line changes
 */
function samePropsAs(
  prev: DiscountRateLineProps,
  next: DiscountRateLineProps
): boolean {
  return (
    prev.line === next.line &&
    prev.lineIndex === next.lineIndex &&
    prev.areaIndex === next.areaIndex &&
    prev.showLabels === next.showLabels &&
    isEqual(prev.enums, next.enums) &&
    prev.onChange === next.onChange &&
    prev.onReserveCategoriesChange === next.onReserveCategoriesChange &&
    prev.onHurdleChange === next.onHurdleChange &&
    prev.onRemove === next.onRemove &&
    prev.overlaps.length === next.overlaps.length &&
    prev.overlaps.every((overlap, i) => overlap === next.overlaps[i])
  );
}

export const DiscountRateLine = memo(DiscountRateLineRow, samePropsAs);
